import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { ThemeProvider } from "styled-components";
import { theme } from "./assets/theme";
import "normalize.css";
// Utilities only — preflight is off, so this adds no global reset. See
// tailwind.config.js for why a third styling system is safe here.
import "./tailwind.css";
import "leaflet/dist/leaflet.css";
import { TractorProvider } from "./contexts/TractorContext";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { MapPreferencesProvider } from "./contexts/MapPreferencesContext";
import { FleetAuthProvider } from "./auth/fleetAuth";
import { startRum } from "./rum";

startRum();

// Validate required environment variables.
// REACT_APP_BACKEND_SERVER_URL may be intentionally empty to opt into
// dev-server-proxy mode (see setupProxy.js + start-beta / start-prod scripts),
// so we only fail when a var is truly undefined, not when it's an empty string.
const requiredEnvVars = {
  REACT_APP_AUTH0_DOMAIN: process.env.REACT_APP_AUTH0_DOMAIN,
  REACT_APP_AUTH0_CLIENT_ID: process.env.REACT_APP_AUTH0_CLIENT_ID,
  REACT_APP_AUTH0_AUDIENCE: process.env.REACT_APP_AUTH0_AUDIENCE,
  REACT_APP_BACKEND_SERVER_URL: process.env.REACT_APP_BACKEND_SERVER_URL,
};

const missingVars = Object.entries(requiredEnvVars)
  .filter(([_, value]) => value === undefined)
  .map(([key]) => key);

// When config is missing, show a visible, actionable message rather than
// throwing (which left a blank white page) or mounting with an undefined Auth0
// config (which then breaks in confusing ways). Only variable NAMES are shown,
// never values. (SW-6328)
function ConfigError({ missing }: { missing: string[] }) {
  return (
    <div
      style={{
        fontFamily: "system-ui, -apple-system, sans-serif",
        maxWidth: 560,
        margin: "80px auto",
        padding: "0 24px",
        color: "#222a35",
        lineHeight: 1.5,
      }}
    >
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>Configuration error</h1>
      <p>
        The app can’t start — these required environment variables are missing:
      </p>
      <ul style={{ fontFamily: "monospace" }}>
        {missing.map((name) => (
          <li key={name}>{name}</li>
        ))}
      </ul>
      <p>
        Add them to <code>fleet_management/frontend/.env</code> (see{" "}
        <code>.env.example</code>), then restart the dev server.
      </p>
    </div>
  );
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

if (missingVars.length > 0) {
  console.error(
    `Missing required environment variables: ${missingVars.join(", ")}`,
  );
  root.render(<ConfigError missing={missingVars} />);
} else {
  root.render(
    <React.StrictMode>
      <FleetAuthProvider
        auth0Domain={requiredEnvVars.REACT_APP_AUTH0_DOMAIN!}
        auth0ClientId={requiredEnvVars.REACT_APP_AUTH0_CLIENT_ID!}
        auth0Audience={requiredEnvVars.REACT_APP_AUTH0_AUDIENCE!}
      >
        <PermissionsProvider>
          <TractorProvider>
            <MapPreferencesProvider>
              <ThemeProvider theme={theme}>
                <App />
              </ThemeProvider>
            </MapPreferencesProvider>
          </TractorProvider>
        </PermissionsProvider>
      </FleetAuthProvider>
    </React.StrictMode>,
  );
}
